import { useCallback, useRef } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'

import axios from 'axios'

import { LIBRARY_TAB } from 'Pods/ThreeDStudio/constants'

const PER_PAGE = 10

// The two tabs read from different endpoints because they list different things: the 3D
// tab lists sessions, the Images tab lists individual image runs (one card per image, not
// per session - a session holds many). Both are expected to answer with the same
// `{ data, meta }` envelope so the pagination below stays shared.
const ENDPOINT = {
  [LIBRARY_TAB.MODELS]: '/api/internal/generator/sessions',
  [LIBRARY_TAB.IMAGES]: '/api/internal/generator/images',
}

const IMAGE_POLL_INTERVAL_MS = 5000

// The images index returns requested and processing runs too, and nothing else refetches
// it when one settles, so the tab polls while any loaded row is still in flight.
const imagesRefetchInterval = (data) => {
  const hasInFlightRun = data?.pages?.some(page => page.items.some(
    ({ attributes }) => attributes?.status !== 'succeeded'
  ))

  return hasInFlightRun ? IMAGE_POLL_INTERVAL_MS : false
}

// Query-key segment per tab. The models tab deliberately keeps the historic 'sessions'
// segment: removeSessionFromLibraryCache and patchSessionMainRunInCache both match on it,
// and renaming it here would silently stop delete and the privacy toggle from updating
// the 3D library in place.
const QUERY_SCOPE = {
  [LIBRARY_TAB.MODELS]: 'sessions',
  [LIBRARY_TAB.IMAGES]: 'images',
}

function useGetLibraryItems(isLoggedIn, keywords, libraryTab = LIBRARY_TAB.MODELS, shouldPoll = true) {
  const normalizedKeywords = (keywords ?? '').trim()
  const shouldUseKeywords = normalizedKeywords.length > 0
  const path = ENDPOINT[libraryTab] ?? ENDPOINT[LIBRARY_TAB.MODELS]
  const scope = QUERY_SCOPE[libraryTab] ?? QUERY_SCOPE[LIBRARY_TAB.MODELS]

  // Only the search/keyword-change path aborts an in-flight request (below).
  // We intentionally do NOT abort on unmount: collapsing the sidebar (e.g. the
  // mobile initial-panel behavior) unmounts LibraryContent mid-fetch, and an
  // abort there both shows a "canceled" request and - because the catch resolves
  // to empty data - poisons the 5-minute-stale React Query cache, leaving the
  // library empty on reopen. Letting the request finish caches the real data.
  const abortControllerRef = useRef(null)
  const lastKeywordsRef = useRef(normalizedKeywords)

  const {
    data,
    hasNextPage: queryHasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isInitialLoading,
    isError,
    error,
  } = useInfiniteQuery({
    // The tab is part of the key so switching tabs does not serve one list's pages as
    // the other's, and so each tab keeps its own cached pages when you switch back.
    queryKey: shouldUseKeywords
      ? ['libraryItems', scope, normalizedKeywords]
      : ['libraryItems', scope],
    queryFn: async ({ pageParam = 1 }) => {
      if (lastKeywordsRef.current !== normalizedKeywords) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }
        lastKeywordsRef.current = normalizedKeywords
      }

      const controller = new AbortController()
      abortControllerRef.current = controller

      try {
        const params = { page: pageParam, per_page: PER_PAGE }
        if (shouldUseKeywords) {
          params.keywords = normalizedKeywords
        }

        const { data: responseData } = await axios.get(path, {
          params,
          signal: controller.signal,
        })

        return {
          items: responseData.data || [],
          meta: responseData.meta || {},
        }
      } catch (caughtError) {
        const isAborted = axios.isCancel(caughtError)
          || caughtError?.code === 'ERR_CANCELED'
          || caughtError?.name === 'CanceledError'

        if (isAborted) {
          return { items: [], meta: {} }
        }

        throw caughtError
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      const { currentPage, totalPages } = lastPage.meta
      return currentPage < totalPages ? allPages.length + 1 : undefined
    },
    initialPageParam: 1,
    enabled: isLoggedIn === true,
    ...(libraryTab === LIBRARY_TAB.IMAGES && shouldPoll && { refetchInterval: imagesRefetchInterval }),
  })

  const items = data?.pages?.flatMap((page) => page.items) ?? []
  const hasNextPage = queryHasNextPage ?? false
  const meta = data?.pages?.slice(-1)[0]?.meta

  // Resolves with the query result, so a caller can read the appended page straight off it.
  const handleLoadMore = useCallback(() => fetchNextPage(), [fetchNextPage])

  return {
    items,
    hasNextPage,
    handleLoadMore,
    isFetchingNextPage,
    isLoading: isInitialLoading,
    isError,
    error,
    meta,
  }
}

export default useGetLibraryItems
